import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { dataRoomService } from '../services/dataRoomService';
import { DataRoom } from '../types/dataRoom';
import { PageHeader } from '../components/common';
import { StatCard } from '../components/dashboard';
import CreateDataRoomModal from '../components/datarooms/CreateDataRoomModal';
import DeleteConfirmModal from '../components/datarooms/DeleteConfirmModal';
import PaymentSuccessModal from '../components/common/PaymentSuccessModal';
import { usePaymentSuccess } from '../hooks/usePaymentSuccess';
import {
  FiFolder,
  FiFile,
  FiUsers,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';

// Type cast icons for React 18 compatibility
const FolderIcon = FiFolder as React.FC<React.SVGProps<SVGSVGElement>>;
const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const UsersIcon = FiUsers as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

const DataRooms: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading: authLoading, error: authError, canAccessApp } = useAuthStatus();
  const { showModal: showPaymentSuccessModal, planTier: paymentPlanTier, closeModal: closePaymentSuccessModal } = usePaymentSuccess();

  // Data state
  const [dataRooms, setDataRooms] = useState<DataRoom[]>([]);
  const [stats, setStats] = useState({
    total_rooms: 0,
    active_rooms: 0,
    draft_rooms: 0,
  });

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingRoom, setDeletingRoom] = useState<DataRoom | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculate stats
  const calculateStats = useCallback((rooms: DataRoom[]) => {
    return {
      total_rooms: rooms.length,
      active_rooms: rooms.filter((r) => r.status === 'active').length,
      draft_rooms: rooms.filter((r) => r.status === 'draft').length,
    };
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rooms = await dataRoomService.getDataRooms();
      setDataRooms(rooms);
      setStats(calculateStats(rooms));
    } catch (err: any) {
      console.error('Failed to load data rooms:', err);
      setError(err?.message || 'Failed to load data rooms');
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  useEffect(() => {
    if (authLoading || authError || !canAccessApp) return;
    loadData();
  }, [authLoading, authError, canAccessApp, loadData]);

  // Handlers
  const handleCreateDataRoom = async (input: { name: string; description?: string }) => {
    try {
      const newRoom = await dataRoomService.createDataRoom(input);
      await loadData();
      // Navigate to the new room
      navigate(`/data-rooms/${newRoom.id}`);
    } catch (err: any) {
      console.error('Failed to create data room:', err);
      setError(err?.message || 'Failed to create data room');
      throw err; // Re-throw so modal can handle it
    }
  };

  const handleDeleteDataRoom = async () => {
    if (!deletingRoom) return;
    setIsDeleting(true);
    try {
      await dataRoomService.deleteDataRoom(deletingRoom.id);
      setDeletingRoom(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to delete data room:', err);
      setError(err?.message || 'Failed to delete data room. Please try again.');
      setDeletingRoom(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCardClick = (room: DataRoom) => {
    navigate(`/data-rooms/${room.id}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, room: DataRoom) => {
    e.stopPropagation();
    setDeletingRoom(room);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Early returns for auth states
  if (authError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4 text-red-600">Authentication Error</h1>
        <p className="text-red-500">{authError}</p>
      </div>
    );
  }

  if (!canAccessApp) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Access Denied</h1>
        <p>You don&apos;t have access to this page.</p>
      </div>
    );
  }

  return (
    <>
      <div className="su-dataroom-page">
        {/* Header */}
        <PageHeader
          title="Data Rooms"
          subtitle="Secure document sharing for due diligence"
          action={
            <button
              className="su-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon />
              <span>CREATE DATA ROOM</span>
            </button>
          }
        />

        {/* Stats */}
        <div className="su-dataroom-stats-grid">
          <StatCard
            icon={<FolderIcon />}
            label="Total Data Rooms"
            value={loading ? '—' : stats.total_rooms}
            colorTheme="purple"
            isLoading={loading}
          />
          <StatCard
            icon={<FileIcon />}
            label="Active Rooms"
            value={loading ? '—' : stats.active_rooms}
            colorTheme="green"
            isLoading={loading}
          />
          <StatCard
            icon={<UsersIcon />}
            label="Draft Rooms"
            value={loading ? '—' : stats.draft_rooms}
            colorTheme="purple"
            isLoading={loading}
          />
        </div>

        {/* Error state */}
        {error && (
          <div
            style={{
              color: '#f87171',
              fontSize: '14px',
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            {error}
            <button
              className="su-btn-secondary"
              style={{ marginLeft: '16px' }}
              onClick={loadData}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="su-dataroom-grid">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="animate-pulse"
                style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #1f1f1f',
                  padding: '20px',
                  height: '140px',
                }}
              >
                <div className="h-5 w-32 bg-gray-800 rounded mb-3" />
                <div className="h-4 w-full bg-gray-800 rounded mb-4" />
                <div className="flex gap-4">
                  <div className="h-3 w-16 bg-gray-800 rounded" />
                  <div className="h-3 w-20 bg-gray-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && dataRooms.length === 0 && (
          <div className="su-dataroom-empty">
            <div className="su-dataroom-empty-icon">
              <FolderIcon style={{ width: 48, height: 48 }} />
            </div>
            <h3 className="su-dataroom-empty-title">No data rooms yet</h3>
            <p className="su-dataroom-empty-subtitle">
              Create secure data rooms for due diligence. Control access, track document views, and manage permissions.
            </p>
            <button
              className="su-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon />
              <span>CREATE YOUR FIRST DATA ROOM</span>
            </button>
          </div>
        )}

        {/* Pro tip banner */}
        {!loading && dataRooms.length === 0 && (
          <div className="su-protip-banner">
            <span className="su-protip-label">Pro Tip:</span>
            Data rooms include visitor tracking, watermarked PDFs, and granular permissions per folder.
          </div>
        )}

        {/* Data rooms grid */}
        {!loading && dataRooms.length > 0 && (
          <div className="su-dataroom-grid">
            {dataRooms.map((room) => (
              <div
                key={room.id}
                className="su-dataroom-card"
                onClick={() => handleCardClick(room)}
              >
                <div className="su-dataroom-card-header">
                  <span className="su-dataroom-card-name">{room.name}</span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span className={room.status === 'active' ? 'su-badge-active' : 'su-badge-draft'}>
                      {room.status}
                    </span>
                    <button
                      type="button"
                      className="su-folder-action-btn delete"
                      onClick={(e) => handleDeleteClick(e, room)}
                      title="Delete data room"
                      aria-label="Delete data room"
                    >
                      <TrashIcon width={14} height={14} />
                    </button>
                  </div>
                </div>
                {room.description && (
                  <p className="su-dataroom-card-desc">{room.description}</p>
                )}
                <div className="su-dataroom-card-meta">
                  <span className="su-dataroom-card-meta-item">
                    <FolderIcon width={12} height={12} />
                    {room.folder_count ?? 0} folders
                  </span>
                  <span className="su-dataroom-card-meta-item">
                    <FileIcon width={12} height={12} />
                    {room.document_count ?? 0} files
                  </span>
                  <span className="su-dataroom-card-meta-item">
                    Created {formatDate(room.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateDataRoomModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateDataRoom}
        />
      )}

      {deletingRoom && (
        <DeleteConfirmModal
          title="Delete Data Room"
          message={`Are you sure you want to delete "${deletingRoom.name}"? This will permanently delete all folders, documents, and share links. This action cannot be undone.`}
          onCancel={() => setDeletingRoom(null)}
          onConfirm={handleDeleteDataRoom}
          isDeleting={isDeleting}
        />
      )}

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showPaymentSuccessModal}
        onClose={closePaymentSuccessModal}
        planTier={paymentPlanTier}
      />
    </>
  );
};

export default DataRooms;
